import { Injectable, inject } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { apiConfig } from '../../b2c-config';

type SessionEvent = 'login' | 'heartbeat' | 'session-end';

interface SessionMetadata {
    event: SessionEvent;
    sessionId: string;
    loginAt: string;
    totalSeconds?: number;
    activeSeconds?: number;
    reason?: string;
}

const HEARTBEAT_MS = 60_000;
const IDLE_MS = 30_000;
const MAX_SESSION_MS = 12 * 60 * 60 * 1_000;
const STORAGE_KEY = 'airia.sessionTracking';
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'] as const;

interface PersistedSession {
    sessionId: string;
    heartbeatAuditId: number | null;
    loginAtMs: number;
    activeMs: number;
}

@Injectable({ providedIn: 'root' })
export class SessionTrackingService {
    private readonly authService = inject(MsalService);

    private readonly auditUrl = `${environment.apiUrl}AuditLog`;

    private sessionId: string | null = null;
    private heartbeatAuditId: number | null = null;
    private heartbeatInFlight = false;
    private loginAtMs = 0;
    private activeMs = 0;
    private lastActivityMs = 0;
    private lastTickMs = 0;
    private bearerToken: string | null = null;

    private heartbeatHandle: ReturnType<typeof setInterval> | null = null;
    private readonly activityListener = (): void => this.recordActivity();
    private readonly visibilityListener = (): void => this.recordActivity();
    private boundUnload: (() => void) | null = null;

    get active(): boolean {
        return this.sessionId !== null;
    }

    startSession(): void {
        if (this.sessionId) {
            this.endSession('reauth');
        }

        const now = Date.now();
        this.lastActivityMs = now;
        this.lastTickMs = now;

        const resumed = this.readPersisted();
        if (resumed) {
            this.sessionId = resumed.sessionId;
            this.heartbeatAuditId = resumed.heartbeatAuditId;
            this.loginAtMs = resumed.loginAtMs;
            this.activeMs = resumed.activeMs;
            this.refreshToken();
        } else {
            this.sessionId = this.newSessionId();
            this.heartbeatAuditId = null;
            this.loginAtMs = now;
            this.activeMs = 0;
            this.persist();
            this.refreshToken().then(() => this.postEventRow('login'));
        }

        this.attachListeners();
    }

    endSession(reason: string): void {
        if (!this.sessionId) return;
        this.tickActive();
        if (reason === 'unload') {
            this.persist();
        } else {
            this.postEventRow('session-end', reason, true);
            this.clearPersisted();
        }
        this.teardown();
    }

    private recordActivity(): void {
        if (!this.sessionId) return;
        this.tickActive();
        this.lastActivityMs = Date.now();
    }

    private tickActive(): void {
        const now = Date.now();
        const visible = document.visibilityState === 'visible';
        const idle = now - this.lastActivityMs > IDLE_MS;
        if (visible && !idle) {
            this.activeMs += now - this.lastTickMs;
        }
        this.lastTickMs = now;
    }

    private attachListeners(): void {
        ACTIVITY_EVENTS.forEach((evt) => document.addEventListener(evt, this.activityListener, { passive: true }));
        document.addEventListener('visibilitychange', this.visibilityListener);

        this.boundUnload = () => this.endSession('unload');
        window.addEventListener('pagehide', this.boundUnload);
        window.addEventListener('beforeunload', this.boundUnload);

        this.heartbeatHandle = setInterval(() => this.heartbeat(), HEARTBEAT_MS);
    }

    private teardown(): void {
        ACTIVITY_EVENTS.forEach((evt) => document.removeEventListener(evt, this.activityListener));
        document.removeEventListener('visibilitychange', this.visibilityListener);
        if (this.boundUnload) {
            window.removeEventListener('pagehide', this.boundUnload);
            window.removeEventListener('beforeunload', this.boundUnload);
            this.boundUnload = null;
        }
        if (this.heartbeatHandle) {
            clearInterval(this.heartbeatHandle);
            this.heartbeatHandle = null;
        }
        this.sessionId = null;
        this.heartbeatAuditId = null;
        this.heartbeatInFlight = false;
    }

    private async heartbeat(): Promise<void> {
        if (!this.sessionId || this.heartbeatInFlight) return;
        this.heartbeatInFlight = true;
        try {
            this.tickActive();
            this.persist();
            await this.refreshToken();
            if (this.heartbeatAuditId === null) {
                await this.createHeartbeatRow();
            } else {
                await this.updateHeartbeatRow();
            }
        } finally {
            this.heartbeatInFlight = false;
        }
    }

    private async refreshToken(): Promise<void> {
        const account = this.authService.instance.getActiveAccount();
        if (!account) return;
        try {
            const result = await firstValueFrom(this.authService.acquireTokenSilent({ account, scopes: [...apiConfig.scopes] }));
            this.bearerToken = result?.accessToken ?? this.bearerToken;
        } catch {}
    }

    private buildMetadata(event: SessionEvent, reason?: string): SessionMetadata {
        const meta: SessionMetadata = {
            event,
            sessionId: this.sessionId!,
            loginAt: new Date(this.loginAtMs).toISOString(),
        };
        if (event !== 'login') {
            meta.totalSeconds = Math.round((Date.now() - this.loginAtMs) / 1000);
            meta.activeSeconds = Math.round(this.activeMs / 1000);
        }
        if (reason) meta.reason = reason;
        return meta;
    }

    private buildBody(event: SessionEvent, reason?: string): string {
        const metadata = this.buildMetadata(event, reason);
        return JSON.stringify({
            auditMessage: this.messageFor(event, metadata),
            auditType: 'Info',
            entityType: 'USER',
            metadata: JSON.stringify(metadata),
        });
    }

    private buildUpdateBody(): string {
        const metadata = this.buildMetadata('heartbeat');
        return JSON.stringify({
            auditMessage: this.messageFor('heartbeat', metadata),
            metadata: JSON.stringify(metadata),
        });
    }

    private authHeaders(): HeadersInit {
        return {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${this.bearerToken}`,
        };
    }

    private postEventRow(event: SessionEvent, reason?: string, useBeacon = false): void {
        if (!this.bearerToken) return;
        fetch(this.auditUrl, {
            method: 'POST',
            keepalive: useBeacon,
            headers: this.authHeaders(),
            body: this.buildBody(event, reason),
        }).catch(() => undefined);
    }

    private async createHeartbeatRow(): Promise<void> {
        if (!this.bearerToken) return;
        try {
            const response = await fetch(this.auditUrl, {
                method: 'POST',
                headers: this.authHeaders(),
                body: this.buildBody('heartbeat'),
            });
            const created = await response.json();
            this.heartbeatAuditId = created?.auditId ?? created?.AuditId ?? created?.id ?? null;
            this.persist();
        } catch {}
    }

    private async updateHeartbeatRow(): Promise<void> {
        if (!this.bearerToken || this.heartbeatAuditId === null) return;
        try {
            await fetch(`${this.auditUrl}/${this.heartbeatAuditId}`, {
                method: 'PUT',
                headers: this.authHeaders(),
                body: this.buildUpdateBody(),
            });
        } catch {}
    }

    private messageFor(event: SessionEvent, meta: SessionMetadata): string {
        const active = this.formatDuration(meta.activeSeconds ?? 0);
        const total = this.formatDuration(meta.totalSeconds ?? 0);
        switch (event) {
            case 'login':
                return 'User session started';
            case 'heartbeat':
                return `User session (${active} active / ${total} total)`;
            case 'session-end':
                return `User session ended (${active} active / ${total} total)`;
        }
    }

    private formatDuration(seconds: number): string {
        const s = Math.max(0, Math.floor(seconds));
        const h = Math.floor(s / 3600);
        const m = Math.floor((s % 3600) / 60);
        const sec = s % 60;
        if (h > 0) return m > 0 ? `${h}h ${m}m` : `${h}h`;
        if (m > 0) return sec > 0 ? `${m}m ${sec}s` : `${m}m`;
        return `${sec}s`;
    }

    private newSessionId(): string {
        if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
            return crypto.randomUUID();
        }
        return `${Date.now().toString(36)}-${Math.floor(Math.random() * 1e9).toString(36)}`;
    }

    private persist(): void {
        if (!this.sessionId) return;
        const data: PersistedSession = {
            sessionId: this.sessionId,
            heartbeatAuditId: this.heartbeatAuditId,
            loginAtMs: this.loginAtMs,
            activeMs: this.activeMs,
        };
        try {
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
        } catch {}
    }

    private readPersisted(): PersistedSession | null {
        try {
            const raw = sessionStorage.getItem(STORAGE_KEY);
            if (!raw) return null;
            const data = JSON.parse(raw) as PersistedSession;
            if (typeof data?.sessionId !== 'string' || typeof data?.loginAtMs !== 'number') return null;
            if (Date.now() - data.loginAtMs > MAX_SESSION_MS) {
                this.clearPersisted();
                return null;
            }
            return data;
        } catch {
            return null;
        }
    }

    private clearPersisted(): void {
        try {
            sessionStorage.removeItem(STORAGE_KEY);
        } catch {}
    }
}
